import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, FileText, Loader2, Smartphone, Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../i18n';
import { Logo } from '../common/Logo';
import { LanguageSwitcher } from '../common/LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { t } = useLang();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('email'); // 'email' | 'phone'
  const [phoneStep, setPhoneStep] = useState(1); // 1 = enter phone, 2 = enter code
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', phone: '', company: '', address: ''
  });

  useEffect(() => {
    if (user) navigate(`/${user.role}`);
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/customer';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send code');
      toast.success(t('otp_sent'));
      setPhoneStep(2);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, code: otpCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      localStorage.setItem('token', data.token);
      toast.success(`Welcome back, ${data.user.name}!`);
      window.location.href = '/customer';
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        if (userData.role !== 'customer') {
          toast.error('This login is for customers only. Staff please use the staff portal.');
          localStorage.removeItem('token');
          window.location.reload();
          return;
        }
        toast.success(`Welcome back, ${userData.name}!`);
        navigate(`/${userData.role}`);
      } else {
        const res = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, role: 'customer' })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Registration failed');
        localStorage.setItem('token', data.token);
        toast.success(`Account created! Welcome, ${data.user.name}!`);
        window.location.href = `/${data.user.role}`;
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8"><Logo /></Link>
        <div className="card p-8 bg-gray-900 border-gray-800">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-600 flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-white" data-testid="login-page-title">{t('customer_portal')}</h2>
                <p className="text-sm text-gray-400">{isLogin ? t('sign_in_subtitle') : t('sign_up_subtitle')}</p>
              </div>
            </div>
            <LanguageSwitcher compact />
          </div>

          {mode === 'phone' ? (
            <div>
              {phoneStep === 1 ? (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <input type="tel" placeholder={t('phone')} className="input" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="otp-phone-input" />
                  <p className="text-[11px] text-gray-500">{t('phone_login_hint')}</p>
                  <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="otp-send-btn">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t('send_code')}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <p className="text-sm text-gray-400">{t('otp_sent')} <span className="text-white font-mono">{phone}</span></p>
                  <input type="text" inputMode="numeric" maxLength={6} placeholder={t('enter_otp')} className="input text-center tracking-[0.5em] font-mono text-lg" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))} required data-testid="otp-code-input" />
                  <button type="submit" disabled={loading || otpCode.length !== 6} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="otp-verify-btn">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t('verify_code')}
                  </button>
                  <button type="button" onClick={() => { setPhoneStep(1); setOtpCode(''); }} className="w-full text-sm text-gray-400 hover:text-red-500 flex items-center justify-center gap-1" data-testid="otp-change-phone-btn">
                    <ArrowLeft className="w-3.5 h-3.5" /> {t('change_phone')}
                  </button>
                </form>
              )}
              <button onClick={() => { setMode('email'); setPhoneStep(1); setOtpCode(''); }} className="mt-4 w-full py-2.5 border border-gray-700 text-gray-300 hover:border-red-600 hover:text-white rounded-lg flex items-center justify-center gap-2 text-sm transition-colors" data-testid="switch-email-login-btn">
                <Mail className="w-4 h-4" /> {t('login_with_email')}
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <>
                    <input type="text" placeholder={t('full_name')} className="input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="register-name-input" />
                    <input type="tel" placeholder={t('phone')} className="input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="register-phone-input" />
                    <input type="text" placeholder={t('company')} className="input" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} data-testid="register-company-input" />
                    <input type="text" placeholder="Address" className="input" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="register-address-input" />
                  </>
                )}
                <input type="email" placeholder={t('email')} className="input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="login-email-input" />
                <input type="password" placeholder={t('password')} className="input" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="login-password-input" />
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="auth-submit-btn">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isLogin ? t('sign_in') : 'Create Account'}
                </button>
              </form>

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-gray-800" />
                <span className="text-xs text-gray-500 uppercase">{t('or_continue')}</span>
                <div className="flex-1 h-px bg-gray-800" />
              </div>
              <div className="space-y-2">
                <button onClick={handleGoogleLogin} className="w-full py-2.5 bg-white hover:bg-gray-100 text-gray-800 font-semibold rounded-lg flex items-center justify-center gap-2 text-sm transition-colors" data-testid="google-login-btn">
                  <GoogleIcon /> {t('sign_in_google')}
                </button>
                {isLogin && (
                  <button onClick={() => setMode('phone')} className="w-full py-2.5 border border-gray-700 text-gray-300 hover:border-red-600 hover:text-white rounded-lg flex items-center justify-center gap-2 text-sm transition-colors" data-testid="phone-login-btn">
                    <Smartphone className="w-4 h-4" /> {t('login_with_phone')}
                  </button>
                )}
              </div>

              <div className="mt-6 text-center">
                <button onClick={() => setIsLogin(!isLogin)} className="text-sm text-gray-400 hover:text-red-500" data-testid="toggle-auth-mode-btn">
                  {isLogin ? t('no_account') : t('has_account')}
                </button>
              </div>
            </>
          )}

          <div className="mt-4 pt-4 border-t border-gray-800">
            <button onClick={() => navigate('/quote')} className="w-full py-3 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition-all" data-testid="get-quote-btn">
              <FileText className="w-5 h-5" />
              {t('get_quote_no_account')}
            </button>
            <p className="text-[11px] text-gray-500 text-center mt-2">{t('get_quote_sub')}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
